/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package i18n

/**
 * Type-safe string keys for internationalization.
 * Each key represents a translatable string in the application.
 */
enum class StringKey {
    // App name and branding
    APP_NAME,
    APP_TAGLINE,

    // Navigation
    NAV_DASHBOARD,
    NAV_TRANSACTIONS,
    NAV_GOALS,
    NAV_BUDGET,
    NAV_SETTINGS,
    NAV_BACK,
    NAV_HOME,

    // Authentication
    AUTH_LOGIN,
    AUTH_LOGOUT,
    AUTH_REGISTER,
    AUTH_EMAIL,
    AUTH_PASSWORD,
    AUTH_CONFIRM_PASSWORD,
    AUTH_FIRST_NAME,
    AUTH_LAST_NAME,
    AUTH_POSTAL_CODE,
    AUTH_PHONE,
    AUTH_DATE_OF_BIRTH,
    AUTH_CREATE_ACCOUNT,
    AUTH_TRY_DEMO,
    DEMO_BANNER,
    AUTH_SIGN_IN,
    AUTH_FORGOT_PASSWORD,
    AUTH_ALREADY_HAVE_ACCOUNT,
    AUTH_NO_ACCOUNT,
    AUTH_CREATING_ACCOUNT,
    AUTH_SIGNING_IN,
    AUTH_PASSWORD_REQUIREMENTS,
    AUTH_PASSWORD_MIN_LENGTH,
    AUTH_PASSWORD_UPPERCASE,
    AUTH_PASSWORD_LOWERCASE,
    AUTH_PASSWORD_NUMBER,
    AUTH_PASSWORDS_MATCH,
    AUTH_PASSWORDS_DONT_MATCH,
    AUTH_MFA_TITLE,
    AUTH_MFA_DESCRIPTION,
    AUTH_MFA_CODE,
    AUTH_MFA_VERIFY,
    AUTH_MFA_VERIFYING,

    // Email verification flow
    AUTH_CHECK_EMAIL_TITLE,
    AUTH_CHECK_EMAIL_SENT,
    AUTH_CHECK_EMAIL_INSTRUCTIONS,
    AUTH_CHECK_EMAIL_GO_TO_LOGIN,
    AUTH_CHECK_EMAIL_RESEND,
    AUTH_CHECK_EMAIL_SENT_SUCCESS,

    // Paywall
    PAYWALL_TITLE,
    PAYWALL_BODY,
    PAYWALL_TITLE_UPGRADE,
    PAYWALL_BODY_UPGRADE,
    PAYWALL_MAYBE_LATER,
    PAYWALL_SUBSCRIBE,
    PAYWALL_READ_ONLY,
    READ_ONLY_ACTIONS_LOCKED,
    PAYWALL_RESTORE,
    PAYWALL_MONTHLY_PRICE,
    PAYWALL_YEARLY_PRICE,
    PAYWALL_MONTHLY_LABEL,
    PAYWALL_YEARLY_LABEL,
    PAYWALL_BEST_VALUE,
    PAYWALL_SAVE_14,
    PAYWALL_SAVE_PREFIX,
    PAYWALL_PER_MONTH_SHORT,
    PAYWALL_BLOCKED_TOAST,
    PAYWALL_BLOCKED_TOAST_CTA,
    PAYWALL_FREE_TRIAL,
    PAYWALL_LOADING_PRODUCTS,
    PAYWALL_PURCHASE_FAILED,
    PAYWALL_RESTORE_SUCCESS,
    PAYWALL_ALREADY_SUBSCRIBED,
    PAYWALL_AUTO_RENEW_DISCLOSURE,
    PAYWALL_TERMS_OF_USE,
    PAYWALL_PRIVACY_POLICY,
    SETTINGS_CONTACT_SUPPORT,
    SETTINGS_VIEW_ON_WEB,
    TERMS_OVERVIEW_BODY,
    TERMS_SUBSCRIPTION_BODY,
    TERMS_ACCEPTABLE_USE_BODY,
    TERMS_LIMITATION_BODY,
    TERMS_CONTACT_BODY,
    AUTH_TERMS_AGREEMENT,

    // Update required
    UPDATE_REQUIRED_TITLE,
    UPDATE_REQUIRED_BODY,
    UPDATE_REQUIRED_BUTTON,

    // Landing page
    LANDING_HERO_TITLE,
    LANDING_HERO_SUBTITLE,
    LANDING_HERO_SUBTITLE_SHORT,
    LANDING_SAVINGS_RATE,
    LANDING_ONE_NUMBER,
    LANDING_INFINITE_POSSIBILITIES,
    LANDING_GET_STARTED,
    LANDING_SEE_HOW_IT_WORKS,
    LANDING_DISCOVER_YOUR_NUMBER,
    LANDING_FIND_MY_SAVINGS_RATE,
    LANDING_BANK_LEVEL_SECURITY,
    LANDING_INSTITUTIONS_COUNT,
    LANDING_NO_CREDIT_CARD,
    LANDING_WHY_ONE_NUMBER,
    LANDING_WHY_ONE_NUMBER_DESC,
    LANDING_SIMPLE_MATH,
    LANDING_SIMPLE_MATH_DESC,
    LANDING_PSYCHOLOGY_TITLE,
    LANDING_PSYCHOLOGY_DESC,
    LANDING_PSYCHOLOGY_STAT,
    LANDING_SMALL_CHANGES,
    LANDING_SMALL_CHANGES_DESC,
    LANDING_EVERYTHING_YOU_NEED,
    LANDING_SEE_REAL_NUMBER,
    LANDING_SEE_REAL_NUMBER_DESC,
    LANDING_UPDATED_REALTIME,
    LANDING_UNDERSTAND_WHAT_MOVES,
    LANDING_UNDERSTAND_DESC,
    LANDING_AI_POWERED,
    LANDING_IMPROVE_AUTOMATICALLY,
    LANDING_IMPROVE_DESC,
    LANDING_PERSONALIZED,
    LANDING_BUILDING_HABITS,
    LANDING_BUILDING_HABITS_DESC,
    LANDING_ADVICE_FOOTER,
    LANDING_PRICING_TITLE,
    LANDING_PRICING_SUBTITLE,
    LANDING_PRICING_CALCULATOR, // unused legacy, kept to avoid renumbering
    LANDING_WHATS_YOUR_NUMBER,
    LANDING_WHATS_YOUR_NUMBER_DESC,
    LANDING_WHATS_YOUR_NUMBER_SUBTEXT,
    LANDING_DATA_SAFE,
    LANDING_PRIVACY_TITLE,
    LANDING_MY_DASHBOARD,

    // Pricing
    PRICING_MONTHLY,
    PRICING_YEARLY,
    PRICING_SAVE_14,
    PRICING_EVERYTHING_YOU_NEED,
    PRICING_START_FREE_TRIAL,
    PRICING_CANCEL_ANYTIME,
    PRICING_FULL_HISTORY,
    PRICING_CROSS_PLATFORM,
    PRICING_CATEGORY_CUSTOMIZATION,

    // Dashboard
    DASHBOARD_TITLE,
    DASHBOARD_SAVINGS_RATE,
    DASHBOARD_THIS_MONTH,
    DASHBOARD_LAST_MONTH,
    DASHBOARD_INCOME,
    DASHBOARD_EXPENSES,
    DASHBOARD_SAVINGS,
    DASHBOARD_BUDGET,
    DASHBOARD_MONTHLY_SUMMARY,
    DASHBOARD_SPENDING_BY_CATEGORY,
    DASHBOARD_TRENDS,
    DASHBOARD_PROJECTIONS,
    DASHBOARD_NO_DATA,
    DASHBOARD_CHART_VIEW,
    DASHBOARD_CATEGORY_SPENDING,
    DASHBOARD_EXPENSE_DISTRIBUTION,
    DASHBOARD_EXPENSE_TIMELINE,
    DASHBOARD_SAVINGS_TREND,
    DASHBOARD_SAVINGS_FOR_MONTH,
    DASHBOARD_SAVINGS_SMOOTHED_INFO,
    DASHBOARD_SAVINGS_RECOMMENDATION,
    DASHBOARD_AVAILABLE_TO_SAVE,
    DASHBOARD_RECOMMENDED_ALLOCATION,
    DASHBOARD_MONTHS_LEFT,
    DASHBOARD_TARGET,
    DASHBOARD_ACTUAL_SAVINGS,
    DASHBOARD_ACCOUNTS,
    DASHBOARD_SELECTED_COUNT,
    DASHBOARD_COMPARISON,
    DASHBOARD_NEEDS_WANTS_FLOW,
    DASHBOARD_AVERAGE_SAVINGS_FOR_YEAR,
    DASHBOARD_SAVINGS_CURRENT_MONTH,
    DASHBOARD_MONTH_IN_PROGRESS,
    DASHBOARD_YTD_AVG_SAVINGS,
    DASHBOARD_INCOME_EXPENSES_SAVINGS,
    DASHBOARD_INCOME_EXPENSES,
    DASHBOARD_AVAILABLE_THIS_MONTH,
    DASHBOARD_OVER_BUDGET_THIS_MONTH,
    DASHBOARD_OVER_BUDGET_REDUCE,
    DASHBOARD_SAVINGS_BY_MONTH,
    DASHBOARD_LINK_ACCOUNTS_SAVINGS,
    DASHBOARD_CREATE_GOAL,
    DASHBOARD_NUDGE_SPEND_LESS,
    DASHBOARD_VIEW_BUDGET,
    DASHBOARD_EARLY_MONTH_HINT,
    DASHBOARD_NO_MONTH_DATA,
    DASHBOARD_EXPENSES_BEFORE_INCOME,
    DASHBOARD_GOAL_SUGGESTION,
    DASHBOARD_EMERGENCY_FUND,
    DASHBOARD_VACATION,
    DASHBOARD_HOME_DOWN_PAYMENT,
    DASHBOARD_RETIREMENT,
    DASHBOARD_EMERGENCY_FUND_DESC,
    DASHBOARD_VACATION_DESC,
    DASHBOARD_HOME_DOWN_PAYMENT_DESC,
    DASHBOARD_RETIREMENT_DESC,
    DASHBOARD_DEMO_BADGE,
    DASHBOARD_DEMO_CONNECT_TITLE,
    DASHBOARD_DEMO_CONNECT_DESC,
    DASHBOARD_DEMO_CONNECT_CTA,
    ACTIVITY_DEMO_MESSAGE,

    // Transactions
    TRANSACTIONS_TITLE,
    TRANSACTIONS_SEARCH,
    TRANSACTIONS_SHOW_PENDING_HOLDS,
    TRANSACTIONS_ALL,
    TRANSACTIONS_INCOME_ONLY,
    TRANSACTIONS_EXPENSES_ONLY,
    TRANSACTIONS_TODAY,
    TRANSACTIONS_YESTERDAY,
    TRANSACTIONS_THIS_WEEK,
    TRANSACTIONS_THIS_MONTH,
    TRANSACTIONS_OLDER,
    TRANSACTIONS_NO_TRANSACTIONS,
    TRANSACTIONS_LOADING,
    TRANSACTIONS_REFRESH,
    TRANSACTIONS_CATEGORY,
    TRANSACTIONS_AMOUNT,
    TRANSACTIONS_DATE,
    TRANSACTIONS_PENDING,
    TRANSACTIONS_SPLIT,
    TRANSACTIONS_DIST,
    TRANSACTIONS_UNSPLIT,
    TRANSACTIONS_EDIT,
    TRANSACTIONS_DETAILS,

    // Goals
    GOALS_TITLE,
    GOALS_ADD_NEW,
    GOALS_TARGET,
    GOALS_CURRENT,
    GOALS_PROGRESS,
    GOALS_DEADLINE,
    GOALS_MONTHLY_CONTRIBUTION,
    GOALS_ON_TRACK,
    GOALS_BEHIND,
    GOALS_AHEAD,
    GOALS_COMPLETE,
    GOALS_YOUR_GOALS,
    GOALS_PERCENT_COMPLETE,
    GOALS_DAYS_LEFT,
    GOALS_DELETE,
    GOALS_EDIT,
    GOALS_SAVE,
    GOALS_CANCEL,
    GOALS_NAME,
    GOALS_DESCRIPTION,
    GOALS_NO_GOALS,
    GOALS_CREATE_FIRST,

    // Budget
    BUDGET_TITLE,
    BUDGET_MONTHLY_BUDGET,
    BUDGET_SET_BUDGET,
    BUDGET_REMAINING,
    BUDGET_SPENT,
    BUDGET_OVER_BUDGET,
    BUDGET_UNDER_BUDGET,
    BUDGET_NO_BUDGET,
    BUDGET_CREATE_PLAN,
    BUDGET_EXPORT_CSV,
    BUDGET_NET_SAVINGS,
    BUDGET_SAVINGS_PERCENT,
    BUDGET_TOTAL_INCOME,
    BUDGET_TOTAL_EXPENSES,

    // Settings
    SETTINGS_TITLE,
    SETTINGS_ACCOUNT,
    SETTINGS_SAVINGS_TARGET,
    SETTINGS_NOTIFICATIONS,
    SETTINGS_PRIVACY,
    SETTINGS_SECURITY,
    SETTINGS_LANGUAGE,
    SETTINGS_LANGUAGE_ENGLISH,
    SETTINGS_LANGUAGE_FRENCH,
    SETTINGS_COUNTRY,
    SETTINGS_APPEARANCE,
    SETTINGS_THEME_SYSTEM,
    SETTINGS_THEME_DARK,
    SETTINGS_THEME_LIGHT,
    SETTINGS_MFA,
    SETTINGS_MFA_ENABLE,
    SETTINGS_MFA_DISABLE,
    SETTINGS_MFA_SETUP,
    SETTINGS_LINKED_ACCOUNTS,
    SETTINGS_ADD_ACCOUNT,
    SETTINGS_REMOVE_ACCOUNT,
    SETTINGS_DATA_EXPORT,
    SETTINGS_DELETE_ACCOUNT,
    SETTINGS_ABOUT,
    SETTINGS_VERSION,
    SETTINGS_TERMS,
    SETTINGS_PRIVACY_POLICY,

    // Account linking
    ACCOUNT_LINKING_TITLE,
    ACCOUNT_LINKING_DESCRIPTION,
    ACCOUNT_LINKING_CONNECT,
    ACCOUNT_LINKING_CONNECTING,
    ACCOUNT_LINKING_FETCHING_HISTORY,
    ACCOUNT_LINKING_SECURITY_ENCRYPTION,
    ACCOUNT_LINKING_SECURITY_NO_CREDENTIALS,
    ACCOUNT_LINKING_SECURITY_READ_ONLY,
    ACCOUNT_LINKING_SECURITY_PLAID,
    ACCOUNT_LINKING_PRIVACY_NOTICE,
    ACCOUNT_LINKING_COMPLETE,
    ACCOUNT_LINKING_PROGRESS,
    ACCOUNT_LINKING_SKIP,
    ACCOUNT_LINKING_DOWNLOADING,
    ACCOUNT_LINKING_PERCENT_COMPLETE,
    ACCOUNT_LINKING_CONNECTION_FAILED,

    // Time periods
    PERIOD_DAY,
    PERIOD_WEEK,
    PERIOD_MONTH,
    PERIOD_QUARTER,
    PERIOD_YEAR,
    PERIOD_ALL_TIME,
    PERIOD_3_MONTHS,
    PERIOD_6_MONTHS,
    PERIOD_12_MONTHS,
    PERIOD_24_MONTHS,

    // Month names
    MONTH_JAN,
    MONTH_FEB,
    MONTH_MAR,
    MONTH_APR,
    MONTH_MAY,
    MONTH_JUN,
    MONTH_JUL,
    MONTH_AUG,
    MONTH_SEP,
    MONTH_OCT,
    MONTH_NOV,
    MONTH_DEC,

    // Full month names
    MONTH_JANUARY,
    MONTH_FEBRUARY,
    MONTH_MARCH,
    MONTH_APRIL,
    MONTH_MAY_FULL,
    MONTH_JUNE,
    MONTH_JULY,
    MONTH_AUGUST,
    MONTH_SEPTEMBER,
    MONTH_OCTOBER,
    MONTH_NOVEMBER,
    MONTH_DECEMBER,

    // Pricing features
    PRICING_UNLIMITED_ACCOUNTS,
    PRICING_REALTIME_TRACKING,
    PRICING_AI_ADVICE_CARDS,
    PRICING_UNLIMITED_GOALS,
    PRICING_GOAL_OPTIMIZATION,

    // Pro Tier
    TIER_STANDARD,
    TIER_PRO,
    TIER_STANDARD_SUBTITLE,
    TIER_PRO_SUBTITLE,
    PRICING_PRO_MONTHLY,
    PRICING_PRO_YEARLY,
    PRICING_PRO_YEARLY_QUARTERLY_NOTE,
    PRICING_SAVE_15,
    PRICING_BEST_VALUE,
    PRICING_EVERYTHING_IN_STANDARD,
    PRICING_BATCH_TRANSACTION_ACTIONS,
    PRICING_MULTI_SELECT_BULK_EDIT,
    PRICING_MULTI_SELECT_AND_BATCH,
    PRICING_TRANSACTION_NOTIFICATIONS,
    PRICING_SPLIT_TRANSACTIONS,
    PRICING_DISTRIBUTE_TRANSACTIONS,
    PRICING_CUSTOMIZABLE_DASHBOARD,
    PRICING_CONTEXT_FILTERS,
    PRICING_MORE_PRO_COMING_SOON,
    PRICING_UPGRADE_TO_PRO,
    PRICING_UPGRADE_NOW,
    PRICING_UPGRADE_SUBTITLE,
    PRO_UPGRADE_PROMPT_TITLE,
    PRO_UPGRADE_PROMPT_MESSAGE,
    PRO_UPGRADE_PROMPT_CTA,

    // Common actions
    ACTION_SAVE,
    ACTION_CANCEL,
    ACTION_DELETE,
    ACTION_EDIT,
    ACTION_CLOSE,
    ACTION_CONFIRM,
    ACTION_SUBMIT,
    ACTION_RETRY,
    ACTION_REFRESH,
    ACTION_SEARCH,
    ACTION_FILTER,
    ACTION_SORT,
    ACTION_CLEAR,
    ACTION_CONTINUE,
    ACTION_BACK,
    ACTION_NEXT,
    ACTION_PREVIOUS,
    ACTION_DONE,

    // Common labels
    LABEL_LOADING,
    LABEL_ERROR,
    LABEL_SUCCESS,
    LABEL_WARNING,
    LABEL_INFO,
    LABEL_YES,
    LABEL_NO,
    LABEL_OK,
    LABEL_OPTIONAL,
    LABEL_REQUIRED,
    LABEL_TOTAL,
    LABEL_AVERAGE,
    LABEL_MONTHLY,
    LABEL_WEEKLY,
    LABEL_DAILY,
    LABEL_YEARLY,

    // Error messages
    ERROR_GENERIC,
    ERROR_NETWORK,
    ERROR_AUTH_FAILED,
    ERROR_INVALID_CREDENTIALS,
    ERROR_INVALID_EMAIL,
    ERROR_INVALID_PASSWORD,
    ERROR_PASSWORDS_DONT_MATCH,
    ERROR_REQUIRED_FIELD,
    ERROR_CONNECTION_FAILED,
    ERROR_SYNC_FAILED,
    ERROR_LOAD_FAILED,

    // Offline
    OFFLINE_BANNER,
    OFFLINE_EDIT_BLOCKED,

    // Success messages
    SUCCESS_SAVED,
    SUCCESS_DELETED,
    SUCCESS_UPDATED,
    SUCCESS_CONNECTED,
    SUCCESS_LOGGED_IN,
    SUCCESS_REGISTERED,

    // Footer
    FOOTER_COPYRIGHT,
    FOOTER_MADE_WITH_LOVE,
    FOOTER_PRODUCT,
    FOOTER_COMPANY,
    FOOTER_LEGAL,
    FOOTER_CONNECT,
    FOOTER_FEATURES,
    FOOTER_PRICING,
    FOOTER_SECURITY,
    FOOTER_FAQ,
    FOOTER_ABOUT,
    FOOTER_BLOG,
    FOOTER_CAREERS,
    FOOTER_CONTACT,
    FOOTER_TERMS,
    FOOTER_PRIVACY,
    FOOTER_COOKIES,

    // Advice cards
    ADVICE_DINING_ALERT,
    ADVICE_NEW_PERSONAL_BEST,
    ADVICE_WEEKEND_TIP,
    ADVICE_GOAL_UPDATE,

    // Chart labels
    CHART_INCOME,
    CHART_EXPENSES,

    CHART_BUDGET,
    CHART_FORECAST,
    CHART_ACTUAL,

    // Formula section
    FORMULA_YOUR_SAVINGS_PERCENT,
    FORMULA_INCOME_MINUS_EXPENSES,

    // Advice card content
    ADVICE_DINING_SPENT,
    ADVICE_DINING_BOOST,
    ADVICE_SEE_DINING,
    ADVICE_PERSONAL_BEST_MESSAGE,
    ADVICE_KEEP_IT_UP,
    ADVICE_SHARE_WIN,
    ADVICE_WEEKEND_ACCOUNT,
    ADVICE_WEEKEND_ACTIVITY,
    ADVICE_BROWSE_EVENTS,
    ADVICE_VACATION_FUND,
    ADVICE_VACATION_MESSAGE,
    ADVICE_VACATION_BOOST,
    ADVICE_ADJUST_ALLOCATION,

    // Security badges
    SECURITY_BANK_ENCRYPTION,
    SECURITY_256_BIT,
    SECURITY_PLAID_PARTNERSHIP,
    SECURITY_TRUSTED_APPS,
    SECURITY_READ_ONLY,
    SECURITY_NO_MOVE_MONEY,
    SECURITY_NO_CREDENTIAL,
    SECURITY_LOGIN_STAYS,

    // Additional footer
    FOOTER_CCPA_NOTICE,
    FOOTER_X,
    FOOTER_INSTAGRAM,
    FOOTER_LINKEDIN,
    FOOTER_EMAIL,

    // Last updated
    LABEL_LAST_UPDATED,

    // Primary Categories
    CATEGORY_INCOME,
    CATEGORY_TRANSFER_IN,
    CATEGORY_TRANSFER_OUT,
    CATEGORY_LOAN_PAYMENTS,
    CATEGORY_BANK_FEES,
    CATEGORY_ENTERTAINMENT,
    CATEGORY_FOOD_AND_DRINK,
    CATEGORY_GENERAL_MERCHANDISE,
    CATEGORY_HOME_IMPROVEMENT,
    CATEGORY_MEDICAL,
    CATEGORY_PERSONAL_CARE,
    CATEGORY_GENERAL_SERVICES,
    CATEGORY_GOVERNMENT_AND_NON_PROFIT,
    CATEGORY_TRANSPORTATION,
    CATEGORY_TRAVEL,
    CATEGORY_RENT_AND_UTILITIES,
    CATEGORY_OTHER,
    CATEGORY_MISCELLANEOUS,
    CATEGORY_FAMILY,
    CATEGORY_FAMILY_CHILD_SUPPORT,
    CATEGORY_FAMILY_SPOUSAL_SUPPORT,
    CATEGORY_FAMILY_ELDER_CARE,
    CATEGORY_FAMILY_OTHER,

    // Sub-categories: Income
    CATEGORY_INCOME_DIVIDENDS,
    CATEGORY_INCOME_INTEREST_EARNED,
    CATEGORY_INCOME_RETIREMENT_PENSION,
    CATEGORY_INCOME_TAX_REFUND,
    CATEGORY_INCOME_UNEMPLOYMENT,
    CATEGORY_INCOME_WAGES,
    CATEGORY_INCOME_OTHER,

    // Sub-categories: Transfer In
    CATEGORY_TRANSFER_IN_CASH_ADVANCES,
    CATEGORY_TRANSFER_IN_DEPOSIT,
    CATEGORY_TRANSFER_IN_INVESTMENT,
    CATEGORY_TRANSFER_IN_SAVINGS,
    CATEGORY_TRANSFER_IN_ACCOUNT,
    CATEGORY_TRANSFER_IN_OTHER,

    // Sub-categories: Transfer Out
    CATEGORY_TRANSFER_OUT_INVESTMENT,
    CATEGORY_TRANSFER_OUT_SAVINGS,
    CATEGORY_TRANSFER_OUT_WITHDRAWAL,
    CATEGORY_TRANSFER_OUT_ACCOUNT,
    CATEGORY_TRANSFER_OUT_OTHER,

    // Sub-categories: Loan Payments
    CATEGORY_LOAN_CAR,
    CATEGORY_LOAN_CREDIT_CARD,
    CATEGORY_LOAN_PERSONAL,
    CATEGORY_LOAN_MORTGAGE,
    CATEGORY_LOAN_STUDENT,
    CATEGORY_LOAN_OTHER,

    // Sub-categories: Bank Fees
    CATEGORY_BANK_ATM,
    CATEGORY_BANK_FOREIGN_TRANSACTION,
    CATEGORY_BANK_INSUFFICIENT_FUNDS,
    CATEGORY_BANK_INTEREST_CHARGE,
    CATEGORY_BANK_OVERDRAFT,
    CATEGORY_BANK_OTHER,

    // Sub-categories: Entertainment
    CATEGORY_ENTERTAINMENT_GAMBLING,
    CATEGORY_ENTERTAINMENT_MUSIC,
    CATEGORY_ENTERTAINMENT_EVENTS,
    CATEGORY_ENTERTAINMENT_TV_MOVIES,
    CATEGORY_ENTERTAINMENT_VIDEO_GAMES,
    CATEGORY_ENTERTAINMENT_OTHER,

    // Sub-categories: Food and Drink
    CATEGORY_FOOD_ALCOHOL,
    CATEGORY_FOOD_COFFEE,
    CATEGORY_FOOD_FAST_FOOD,
    CATEGORY_FOOD_GROCERIES,
    CATEGORY_FOOD_RESTAURANT,
    CATEGORY_FOOD_VENDING,
    CATEGORY_FOOD_OTHER,

    // Sub-categories: General Merchandise
    CATEGORY_MERCHANDISE_BOOKS,
    CATEGORY_MERCHANDISE_CLOTHING,
    CATEGORY_MERCHANDISE_CONVENIENCE,
    CATEGORY_MERCHANDISE_DEPARTMENT,
    CATEGORY_MERCHANDISE_DISCOUNT,
    CATEGORY_MERCHANDISE_ELECTRONICS,
    CATEGORY_MERCHANDISE_GIFTS,
    CATEGORY_MERCHANDISE_OFFICE,
    CATEGORY_MERCHANDISE_ONLINE,
    CATEGORY_MERCHANDISE_PET,
    CATEGORY_MERCHANDISE_SPORTING,
    CATEGORY_MERCHANDISE_SUPERSTORES,
    CATEGORY_MERCHANDISE_TOBACCO,
    CATEGORY_MERCHANDISE_OTHER,

    // Sub-categories: Home Improvement
    CATEGORY_HOME_FURNITURE,
    CATEGORY_HOME_HARDWARE,
    CATEGORY_HOME_REPAIR,
    CATEGORY_HOME_SECURITY,
    CATEGORY_HOME_OTHER,

    // Sub-categories: Medical
    CATEGORY_MEDICAL_DENTAL,
    CATEGORY_MEDICAL_EYE,
    CATEGORY_MEDICAL_NURSING,
    CATEGORY_MEDICAL_PHARMACY,
    CATEGORY_MEDICAL_PRIMARY,
    CATEGORY_MEDICAL_VETERINARY,
    CATEGORY_MEDICAL_OTHER,

    // Sub-categories: Personal Care
    CATEGORY_PERSONAL_GYM,
    CATEGORY_PERSONAL_HAIR_BEAUTY,
    CATEGORY_PERSONAL_LAUNDRY,
    CATEGORY_PERSONAL_OTHER,

    // Sub-categories: General Services
    CATEGORY_SERVICES_ACCOUNTING,
    CATEGORY_SERVICES_AUTOMOTIVE,
    CATEGORY_SERVICES_CHILDCARE,
    CATEGORY_SERVICES_LEGAL,
    CATEGORY_SERVICES_EDUCATION,
    CATEGORY_SERVICES_INSURANCE,
    CATEGORY_SERVICES_POSTAGE,
    CATEGORY_SERVICES_STORAGE,
    CATEGORY_SERVICES_OTHER,

    // Sub-categories: Government and Non-Profit
    CATEGORY_GOVERNMENT_DONATIONS,
    CATEGORY_GOVERNMENT_AGENCIES,
    CATEGORY_GOVERNMENT_TAX,
    CATEGORY_GOVERNMENT_OTHER,

    // Sub-categories: Transportation
    CATEGORY_TRANSPORT_BIKES,
    CATEGORY_TRANSPORT_GAS,
    CATEGORY_TRANSPORT_PARKING,
    CATEGORY_TRANSPORT_PUBLIC,
    CATEGORY_TRANSPORT_RIDESHARE,
    CATEGORY_TRANSPORT_TOLLS,
    CATEGORY_TRANSPORT_OTHER,

    // Sub-categories: Travel
    CATEGORY_TRAVEL_AIRLINES,
    CATEGORY_TRAVEL_LODGING,
    CATEGORY_TRAVEL_RENTAL_CARS,
    CATEGORY_TRAVEL_AGENCIES,
    CATEGORY_TRAVEL_OTHER,

    // Sub-categories: Rent and Utilities
    CATEGORY_UTILITIES_GAS_ELECTRIC,
    CATEGORY_UTILITIES_INTERNET,
    CATEGORY_UTILITIES_RENT,
    CATEGORY_UTILITIES_SEWAGE,
    CATEGORY_UTILITIES_TELEPHONE,
    CATEGORY_UTILITIES_WATER,
    CATEGORY_UTILITIES_OTHER,

    // Guide page
    GUIDE_BACK,
    GUIDE_TITLE,
    GUIDE_SUBTITLE,
    GUIDE_STEP1_TITLE,
    GUIDE_STEP1_DESC,
    GUIDE_STEP2_TITLE,
    GUIDE_STEP2_DESC,
    GUIDE_STEP3_TITLE,
    GUIDE_STEP3_DESC,
    GUIDE_STEP4_TITLE,
    GUIDE_STEP4_DESC,
    GUIDE_PRO_TIPS,
    GUIDE_TIP_TRACK,
    GUIDE_TIP_ONE_GOAL,
    GUIDE_TIP_CHECK_IN,
    GUIDE_TIP_BUDGET,
    GUIDE_TIP_AI,
    GUIDE_CTA,

    // Subscribe page
    SUBSCRIBE_CHOOSE_PLAN,
    SUBSCRIBE_TRIAL_SUBTITLE,
    SUBSCRIBE_TRIALING_SUBTITLE,
    SUBSCRIBE_MONTHLY,
    SUBSCRIBE_YEARLY,
    SUBSCRIBE_PER_MONTH,
    SUBSCRIBE_PER_YEAR,
    SUBSCRIBE_TRIAL_INCLUDED,
    SUBSCRIBE_START_FREE_TRIAL,
    SUBSCRIBE_BEST_VALUE,
    SUBSCRIBE_SAVE_PERCENT,
    SUBSCRIBE_TRIAL_DISCLAIMER,
    SUBSCRIBE_SUCCESS_TITLE,
    SUBSCRIBE_SUCCESS_DESC,
    SUBSCRIBE_GO_DASHBOARD,
    SUBSCRIBE_CANCEL_TITLE,
    SUBSCRIBE_CANCEL_DESC,
    SUBSCRIBE_TRY_AGAIN,
    SUBSCRIBE_GO_BACK,

    // Subscription banner
    BANNER_FREE_TRIAL,
    BANNER_DAYS_REMAINING,
    BANNER_DAY_REMAINING,
    BANNER_SUBSCRIBE_NOW,
    BANNER_TRIAL_ENDED,
    BANNER_READ_ONLY,
    BANNER_SUBSCRIBE_UNLOCK,

    // Account re-link
    ACCOUNT_RELINK_TITLE,
    ACCOUNT_RELINK_DESC,
    ACCOUNT_RELINK_BUTTON,

    // Reset password
    RESET_PASSWORD_TITLE,
    RESET_PASSWORD_SUBTITLE,
    RESET_PASSWORD_EMAIL_PLACEHOLDER,
    RESET_PASSWORD_NEW_PASSWORD_PLACEHOLDER,
    RESET_PASSWORD_CONFIRM_PLACEHOLDER,
    RESET_PASSWORD_SUBMIT,
    RESET_PASSWORD_BACK_TO_LOGIN,
    RESET_PASSWORD_ENTER_EMAIL,
    RESET_PASSWORD_MIN_LENGTH,
    RESET_PASSWORD_SUCCESS,
    RESET_PASSWORD_FAILED,

    // Settings extras
    SETTINGS_CHOOSE_LANGUAGE,
    SETTINGS_ACCOUNT_ACTIONS,
    SETTINGS_LOGOUT_DESC,
    SETTINGS_DANGER_ZONE,
    SETTINGS_DELETE_ACCOUNT_DESC,
    SETTINGS_DELETE_CONFIRM_TITLE,
    SETTINGS_DELETE_CONFIRM_DESC,
    SETTINGS_DELETE_CONFIRM_LIST_ACCOUNTS,
    SETTINGS_DELETE_CONFIRM_LIST_TRANSACTIONS,
    SETTINGS_DELETE_CONFIRM_LIST_GOALS,
    SETTINGS_DELETE_CONFIRM_LIST_BUDGETS,
    SETTINGS_DELETE_CONFIRM_LIST_SUBSCRIPTION,
    SETTINGS_DELETE_CANNOT_UNDO,
    SETTINGS_DELETE_FOREVER,
    SETTINGS_DELETING,
    // Re-auth prompt for destructive delete — server requires password proof.
    SETTINGS_DELETE_REAUTH_TITLE,
    SETTINGS_DELETE_REAUTH_MESSAGE,
    SETTINGS_DELETE_REAUTH_PASSWORD_PLACEHOLDER,
    SETTINGS_DELETE_REAUTH_EMPTY_PASSWORD,
    SETTINGS_DELETE_REAUTH_INCORRECT_PASSWORD,
    SETTINGS_DELETE_REAUTH_RATE_LIMITED,
    SETTINGS_DELETE_REAUTH_GENERIC_ERROR,

    // MFA extras
    MFA_BACKUP_CODE_HINT,
    MFA_BACK_TO_LOGIN,
    MFA_ENTER_VERIFICATION_CODE,
    MFA_VERIFICATION_CODE_LABEL,
    MFA_BACKUP_CODES_TITLE,
    MFA_BACKUP_CODES_DESC,
    MFA_ENABLE,
    MFA_DISABLE_TITLE,
    MFA_DISABLE_DESC,
    MFA_ENABLED_DESC,
    MFA_DISABLED_DESC,
    MFA_ENABLED,
    MFA_DISABLED,
    MFA_ENTER_CODE_ERROR,
    MFA_SCAN_QR,
    MFA_MANUAL_CODE,

    // Category display names (virtual categories used in dashboard)
    CATEGORY_DISPLAY_SHOPPING,
    CATEGORY_DISPLAY_HEALTHCARE,
    CATEGORY_DISPLAY_SERVICES,
    CATEGORY_DISPLAY_TRANSFERS_OUT,
    CATEGORY_DISPLAY_TRANSFERS_IN,
    CATEGORY_DISPLAY_HOME,
    CATEGORY_DISPLAY_GOVERNMENT,
    CATEGORY_DISPLAY_RENT_MORTGAGE,
    CATEGORY_DISPLAY_UTILITIES,
    CATEGORY_DISPLAY_SUBSCRIPTIONS,
    CATEGORY_DISPLAY_INSURANCE,

    // Transaction detail
    TRANSACTION_DETAILS_TITLE,
    TRANSACTION_CATEGORY,
    TRANSACTION_ACCOUNT,
    TRANSACTION_DEBIT,
    TRANSACTION_CREDIT,
    TRANSACTION_GOAL,
    TRANSACTION_ACTIONS,
    TRANSACTION_CHANGE_CATEGORY,
    TRANSACTION_UNSPLIT,
    TRANSACTION_UNDISTRIBUTE,
    TRANSACTION_SPLIT,
    TRANSACTION_DISTRIBUTE,
    TRANSACTION_MOVE,
    TRANSACTION_MOVE_TO,
    TRANSACTION_MOVE_PREVIEW,
    TRANSACTION_MOVED_BADGE,
    TRANSACTION_MOVED_DESC,
    TRANSACTION_MOVED_TITLE,
    TRANSACTION_DISTRIBUTED_TITLE,
    TRANSACTION_DISTRIBUTED_DESC,
    TRANSACTION_DISTRIBUTE_TOTAL,
    TRANSACTION_DISTRIBUTE_START_MONTH,
    TRANSACTION_DISTRIBUTE_END_MONTH,
    TRANSACTION_DISTRIBUTE_EACH_MONTH,
    TRANSACTION_DISTRIBUTE_PREVIEW,
    TRANSACTION_DISTRIBUTE_MONTHS,
    TRANSACTION_SPLIT_TITLE,
    TRANSACTION_SPLIT_MANUAL_ENTRY,
    TRANSACTION_SPLIT_CSV_IMPORT,
    TRANSACTION_SPLIT_ITEM,
    TRANSACTION_SPLIT_AMOUNT,
    TRANSACTION_SPLIT_AMOUNT_CREDIT,
    TRANSACTION_SPLIT_NOTE_OPTIONAL,
    TRANSACTION_SPLIT_NOTE_PLACEHOLDER,
    TRANSACTION_SPLIT_ADD_ITEM,
    TRANSACTION_SPLIT_REMOVE,
    TRANSACTION_SPLIT_REMAINING_WARNING,
    TRANSACTION_SPLIT_OVER_WARNING,
    TRANSACTION_GOAL_CREATE,
    TRANSACTION_GOAL_REMAINING,
    TRANSACTION_NOTE_TAKE,
    TRANSACTION_ADD_NOTE,
    TRANSACTION_NOT_FOUND,
    TRANSACTION_SESSION_EXPIRED,

    // Misc
    LABEL_AVAILABLE,
    LABEL_AVAILABLE_BALANCE,
    LABEL_BANK_ACCOUNTS,
    LABEL_CONNECTION_ERROR,
    LABEL_LOADING_CHART,
    LABEL_SETTINGS,
    LABEL_AND_X_MORE,

    // Chart titles & legends (Dashboard)
    CHART_SAVINGS_TREND_ROLLING,
    CHART_SAVINGS_TREND_YEAR,
    CHART_TARGET_LABEL,
    CHART_ACTUAL_SAVINGS_PERCENT,
    CHART_TARGET_PERCENT,
    CHART_CATEGORY_SPENDING_MONTH,
    CHART_NO_SPENDING_YET,
    CHART_LOADING_TRANSACTIONS,
    CHART_EXPENSE_DIST_ROLLING,
    CHART_EXPENSE_DIST_YEAR,
    CHART_NO_DATA_ACCOUNTS,
    CHART_EXPENSE_TIMELINE_ROLLING,
    CHART_EXPENSE_TIMELINE_YEAR,
    CHART_NO_EXPENSE_DATA,
    CHART_BUDGET_LEGEND,
    CHART_ACTUAL_LEGEND,
    CHART_OVER_BUDGET_LEGEND,
    CHART_TOGGLE_BUDGET,
    CHART_PILL_ROLLING_12MO,
    CHART_PILL_SELECTED_MONTH,
    CHART_UNDER_FORECAST,
    CHART_OVER_FORECAST,
    CHART_TARGET_TOOLTIP,

    // Dashboard recommendations
    DASHBOARD_RECOMMENDED_ALLOCATION_HEADER,
    DASHBOARD_TIME_DAYS_LEFT,
    DASHBOARD_TIME_MONTHS_LEFT,

    // Goals page
    GOALS_NO_GOALS_TITLE,
    GOALS_NO_GOALS_DESC,
    GOALS_LOADING,
    GOALS_PRIORITY_MUST_HAVE,
    GOALS_PRIORITY_NICE_TO_HAVE,
    GOALS_PRIORITY_STRETCH_GOAL,
    GOALS_PRIORITY_MAYBE,
    GOALS_PER_MONTH,
    GOALS_TARGET_DATE,
    GOALS_CREATED_ON,

    // Goals encouragement
    GOALS_ENCOURAGE_COMPLETE,
    GOALS_ENCOURAGE_ALMOST,
    GOALS_ENCOURAGE_HALFWAY,
    GOALS_ENCOURAGE_DEADLINE_SOON,
    GOALS_ENCOURAGE_FEW_MONTHS,
    GOALS_ENCOURAGE_ON_TRACK,
    GOALS_ENCOURAGE_EVERY_BIT,

    // Financial tips
    TIPS_TITLE,
    TIPS_SEARCH_PLACEHOLDER,
    TIPS_PINNED,
    TIPS_PIN_THIS,
    TIPS_WHY_IT_WORKS,
    TIPS_EXAMPLE,
    TIPS_GOT_IT,

    // Financial tips - Why it works content
    TIPS_WHY_50_30_20,
    TIPS_WHY_AUTOMATE,
    TIPS_WHY_24_HOUR,
    TIPS_WHY_COMPOUND,
    TIPS_WHY_SMALL_WINS,
    TIPS_WHY_EMERGENCY,
    TIPS_WHY_TFSA,
    TIPS_WHY_RRSP,
    TIPS_WHY_FHSA,
    TIPS_WHY_ACCOUNTS,
    TIPS_WHY_DEFAULT,

    // Financial tips - Example labels
    TIPS_EXAMPLE_MONTHLY_INCOME,
    TIPS_EXAMPLE_NEEDS,
    TIPS_EXAMPLE_WANTS,
    TIPS_EXAMPLE_SAVINGS,
    TIPS_EXAMPLE_PAYDAY,
    TIPS_EXAMPLE_AUTO_TRANSFER,
    TIPS_EXAMPLE_YEARLY_SAVINGS,
    TIPS_EXAMPLE_MONTHLY_CONTRIBUTION,
    TIPS_EXAMPLE_TIME_PERIOD,
    TIPS_EXAMPLE_AT_RETURN,
    TIPS_EXAMPLE_DAILY_SAVINGS,
    TIPS_EXAMPLE_WEEKLY_TOTAL,
    TIPS_EXAMPLE_YEARLY_TOTAL,
    TIPS_EXAMPLE_MONTHLY_EXPENSES,
    TIPS_EXAMPLE_3_MONTH_FUND,
    TIPS_EXAMPLE_6_MONTH_FUND,
    TIPS_EXAMPLE_CONTRIBUTION_ROOM,
    TIPS_EXAMPLE_WITHDRAWALS,
    TIPS_EXAMPLE_TAX_FREE,
    TIPS_EXAMPLE_BEST_FOR,
    TIPS_EXAMPLE_FLEXIBLE_SAVINGS,
    TIPS_EXAMPLE_CONTRIBUTION_LIMIT,
    TIPS_EXAMPLE_TAX_BENEFIT,
    TIPS_EXAMPLE_DEDUCTIBLE_NOW,
    TIPS_EXAMPLE_RETIREMENT,
    TIPS_EXAMPLE_ANNUAL_LIMIT,
    TIPS_EXAMPLE_LIFETIME_MAX,
    TIPS_EXAMPLE_FIRST_HOME,
    TIPS_EXAMPLE_15TH_OF_MONTH,
    TIPS_EXAMPLE_20_YEARS,

    // Goal dialog
    GOAL_DIALOG_TITLE,
    GOAL_DIALOG_NAME_LABEL,
    GOAL_DIALOG_NAME_PLACEHOLDER,
    GOAL_DIALOG_AMOUNT_LABEL,
    GOAL_DIALOG_DATE_LABEL,
    GOAL_DIALOG_CATEGORY_LABEL,
    GOAL_DIALOG_PRIORITY_LABEL,
    GOAL_DIALOG_ICON_LABEL,
    GOAL_DIALOG_CREATE,

    // Goal categories (dialog)
    GOAL_CAT_EMERGENCY_FUND,
    GOAL_CAT_VACATION,
    GOAL_CAT_HOME_PURCHASE,
    GOAL_CAT_RETIREMENT,
    GOAL_CAT_EDUCATION,
    GOAL_CAT_MAJOR_PURCHASE,
    GOAL_CAT_OTHER,

    // Goal priorities (dialog)
    GOAL_PRI_MUST_HAVE,
    GOAL_PRI_NICE_TO_HAVE,
    GOAL_PRI_STRETCH_GOAL,
    GOAL_PRI_MAYBE,

    // Budget planning
    BUDGET_COPY_FROM_YEAR,
    BUDGET_LOGIN_TO_COPY,
    BUDGET_COPY_SUCCESS,
    BUDGET_CATEGORY_CSV,

    // Recommendations page
    RECO_TITLE,
    RECO_OPTIMIZING,
    RECO_NO_GOALS_TITLE,
    RECO_NO_GOALS_DESC,
    RECO_UNABLE_TO_GENERATE,
    RECO_PORTFOLIO_SUMMARY,
    RECO_MONTHLY_ALLOCATION,
    RECO_SMART_SAVINGS,
    RECO_SMART_SAVINGS_DESC,
    RECO_TOTAL_NEEDED,
    RECO_AVAILABLE_MONTH,
    RECO_OPTIMIZATION_SCORE,
    RECO_FORECASTED_INCOME,
    RECO_FORECASTED_EXPENSES,
    RECO_FUNDING_GAP,
    RECO_FUNDING_GAP_DESC,
    RECO_PLAN_FEASIBLE,
    RECO_PLAN_FEASIBLE_DESC,
    RECO_PER_MONTH,
    RECO_FULLY_FUNDED,
    RECO_PERCENT_OF_NEED,
    RECO_COMPLETE_IN,
    RECO_COMPLETE_NEVER,
    RECO_COMPLETE_MONTHS,
    RECO_CURRENT,
    RECO_CUT,
    RECO_ALLOCATION_ACROSS_GOALS,

    // Advice card categories
    ADVICE_CAT_SAVINGS_TIPS,
    ADVICE_CAT_GOAL_SETTING,
    ADVICE_CAT_ACCOUNTS,
    ADVICE_CAT_ENCOURAGEMENT,
    ADVICE_CAT_BEHAVIORAL,

    // Advice card titles and content
    ADVICE_TIP_50_30_20_TITLE,
    ADVICE_TIP_50_30_20_CONTENT,
    ADVICE_TIP_AUTOMATE_TITLE,
    ADVICE_TIP_AUTOMATE_CONTENT,
    ADVICE_TIP_24_HOUR_TITLE,
    ADVICE_TIP_24_HOUR_CONTENT,
    ADVICE_GOAL_EMERGENCY_TITLE,
    ADVICE_GOAL_EMERGENCY_CONTENT,
    ADVICE_GOAL_SMART_TITLE,
    ADVICE_GOAL_SMART_CONTENT,
    ADVICE_GOAL_VISUALIZE_TITLE,
    ADVICE_GOAL_VISUALIZE_CONTENT,
    ADVICE_ACCOUNT_TFSA_TITLE,
    ADVICE_ACCOUNT_TFSA_CONTENT,
    ADVICE_ACCOUNT_RRSP_TITLE,
    ADVICE_ACCOUNT_RRSP_CONTENT,
    ADVICE_ACCOUNT_FHSA_TITLE,
    ADVICE_ACCOUNT_FHSA_CONTENT,
    ADVICE_ACCOUNT_401K_TITLE,
    ADVICE_ACCOUNT_401K_CONTENT,
    ADVICE_ACCOUNT_ROTH_IRA_TITLE,
    ADVICE_ACCOUNT_ROTH_IRA_CONTENT,
    ADVICE_ACCOUNT_TRAD_IRA_TITLE,
    ADVICE_ACCOUNT_TRAD_IRA_CONTENT,
    ADVICE_ACCOUNT_HSA_TITLE,
    ADVICE_ACCOUNT_HSA_CONTENT,
    ADVICE_ENCOURAGE_COMPOUND_TITLE,
    ADVICE_ENCOURAGE_COMPOUND_CONTENT,
    ADVICE_ENCOURAGE_SMALL_WINS_TITLE,
    ADVICE_ENCOURAGE_SMALL_WINS_CONTENT,
    ADVICE_ENCOURAGE_DOING_GREAT_TITLE,
    ADVICE_ENCOURAGE_DOING_GREAT_CONTENT,
    ADVICE_BEHAVIORAL_LIFESTYLE_TITLE,
    ADVICE_BEHAVIORAL_LIFESTYLE_CONTENT,
    ADVICE_BEHAVIORAL_PAY_YOURSELF_TITLE,
    ADVICE_BEHAVIORAL_PAY_YOURSELF_CONTENT,
    ADVICE_BEHAVIORAL_UNSUBSCRIBE_TITLE,
    ADVICE_BEHAVIORAL_UNSUBSCRIBE_CONTENT,

    // Goal category display names (no emoji)
    GOAL_DISPLAY_EMERGENCY_FUND,
    GOAL_DISPLAY_VACATION,
    GOAL_DISPLAY_HOME_PURCHASE,
    GOAL_DISPLAY_RETIREMENT,
    GOAL_DISPLAY_EDUCATION,
    GOAL_DISPLAY_MAJOR_PURCHASE,
    GOAL_DISPLAY_INVESTMENT,
    GOAL_DISPLAY_DEBT_PAYOFF,
    GOAL_DISPLAY_OTHER,

    // Budget category translations
    BUDGET_CAT_SALARY,
    BUDGET_CAT_TIPS,
    BUDGET_CAT_SIDE_HUSTLES,
    BUDGET_CAT_FINANCIAL_AID,
    BUDGET_CAT_INVESTMENTS,
    BUDGET_CAT_OTHER_INCOME,
    BUDGET_CAT_RENT_MORTGAGE,
    BUDGET_CAT_GROCERIES,
    BUDGET_CAT_TRANSPORTATION,
    BUDGET_CAT_UTILITIES,
    BUDGET_CAT_CLOTHING,
    BUDGET_CAT_PHARMACY_MEDICAL,
    BUDGET_CAT_SUBSCRIPTIONS,
    BUDGET_CAT_SPORTS_FITNESS,
    BUDGET_CAT_VACATION,
    BUDGET_CAT_GIFTS,
    BUDGET_CAT_SCHOOL_EDUCATION,
    BUDGET_CAT_FOOD_DINING_OUT,
    BUDGET_CAT_ENTERTAINMENT,
    BUDGET_CAT_INSURANCE,
    BUDGET_CAT_PERSONAL_CARE,
    BUDGET_CAT_MISCELLANEOUS,

    // Personalized advice cards (frontend)
    ADVICE_PERSONALIZED_INSIGHTS,
    ADVICE_BASED_ON_DAYS,
    ADVICE_ANALYZING_PATTERNS,
    ADVICE_NO_ADVICE_YET,
    ADVICE_KEEP_TRACKING,
    ADVICE_DISMISS,

    // Account staleness / relink
    ACCOUNT_RECONNECT_NEEDED,
    ACCOUNT_NO_NEW_DATA_DAYS,
    ACCOUNT_BANK_NOT_RETURNING_DATA,
    ACCOUNT_RECONNECT_ACTION,
    RELINK_FALLBACK_TITLE,
    RELINK_FALLBACK_BODY,
    RELINK_FALLBACK_PROCEED,
    RELINK_FALLBACK_CANCEL,
    CONNECT_FAILED_TITLE,
    CONNECT_FAILED_BODY,
    CONNECT_FAILED_DISMISS,

    // Account custom naming
    ACCOUNT_RENAME,
    ACCOUNT_TYPE_CHEQUING,
    ACCOUNT_TYPE_SAVINGS,
    ACCOUNT_TYPE_CREDIT_CARD,
    ACCOUNT_TYPE_TFSA,
    ACCOUNT_TYPE_RRSP,
    ACCOUNT_TYPE_FHSA,
    ACCOUNT_TYPE_RESP,
    ACCOUNT_TYPE_RDSP,
    ACCOUNT_TYPE_RRIF,
    ACCOUNT_TYPE_LIRA,
    ACCOUNT_TYPE_BROKERAGE,
    ACCOUNT_TYPE_MORTGAGE,
    ACCOUNT_TYPE_GIC,
    ACCOUNT_TYPE_HSA,
    ACCOUNT_TYPE_MONEY_MARKET,
    ACCOUNT_TYPE_CASH_MANAGEMENT,
    ACCOUNT_TYPE_401K,
    ACCOUNT_TYPE_IRA,
    ACCOUNT_TYPE_LINE_OF_CREDIT,
    ACCOUNT_TYPE_AUTO_LOAN,
    ACCOUNT_TYPE_STUDENT_LOAN,
    ACCOUNT_TYPE_DEPOSITORY,
    ACCOUNT_TYPE_CREDIT,
    ACCOUNT_TYPE_LOAN,
    ACCOUNT_TYPE_INVESTMENT,
    ACCOUNT_TYPE_OTHER,
    ACCOUNT_RENAME_PLACEHOLDER,
    ACCOUNT_RENAME_HINT,
    ACCOUNT_RENAME_DUPLICATE,
    ACCOUNT_RENAME_TOO_LONG,
    ACCOUNT_RENAME_NOT_FOUND,
    ACCOUNT_RENAME_FORBIDDEN,

    // Activity screen strings
    ACTIVITY_NO_ACCOUNTS,
    ACTIVITY_ACCOUNTS,
    ACTIVITY_RECENT_ACTIVITY,
    ACTIVITY_VIEW_ALL,
    ACTIVITY_GOAL,
    ACTIVITY_THIS_MONTH,
    ACTIVITY_TOP_CATEGORIES,
    ACTIVITY_ACCOUNT,
    ACTIVITY_TIP_SEE_MORE,
    ACTIVITY_DEMO_ACCOUNT,
    ACTIVITY_UNKNOWN,
    ACTIVITY_ERROR_LOADING,
    ACTIVITY_FETCHING,
    ACTIVITY_LAST_UPDATED_JUST_NOW,
    ACTIVITY_LAST_UPDATED_ONE_MINUTE,
    ACTIVITY_LAST_UPDATED_MINUTES,
    ACTIVITY_LAST_UPDATED_ONE_HOUR,
    ACTIVITY_LAST_UPDATED_HOURS,
    ACTIVITY_SYNC,
    ACTIVITY_SYNCING,
    ACTIVITY_AVAILABLE_BALANCE,
    ACTIVITY_FILTERED_BY,
    ACTIVITY_EXPENSES,
    ACTIVITY_NET,
    ACTIVITY_VERIFY_CATEGORY,

    // Confidence labels
    CONFIDENCE_AI,
    CONFIDENCE_USER,

    // Chart visibility states
    CHART_VISIBILITY_OPEN,
    CHART_VISIBILITY_CLOSED,
    CHART_VISIBILITY_HIDDEN,

    // Settings section headers
    SETTINGS_SECTION_PROFILE,
    SETTINGS_SECTION_SAVINGS_GOAL,
    SETTINGS_SECTION_CHARTS,
    SETTINGS_SECTION_PREFERENCES,
    SETTINGS_SECTION_SUPPORT,
    SETTINGS_SECTION_FINANCIAL,

    // Auth screen
    AUTH_USERNAME,
    AUTH_USERNAME_PLACEHOLDER,
    AUTH_EMAIL_PLACEHOLDER,
    AUTH_PASSWORD_PLACEHOLDER,
    AUTH_OR_SEPARATOR,

    // Budget / Charts screen labels
    CHART_HIGHEST_SPENDING,
    CHART_AVERAGE_PER_CATEGORY,
    CHART_SPENDING_PATTERN,
    BUDGET_SECTION_SUMMARY,

    // Transaction detail labels
    TRANSACTION_LABEL_SUBCATEGORY,
    TRANSACTION_MANUAL_ENTRY,
    TRANSACTION_CSV_IMPORT,
    TRANSACTION_SAVE_SPLIT_ACTION,
    TRANSACTION_NOTE_PLACEHOLDER,
    TRANSACTION_ASSIGN_GOAL,
    TRANSACTION_REMOVE_GOAL,
    TRANSACTION_CHANGE_CATEGORY_DESC,
    TRANSACTION_ASSIGN_GOAL_DESC,
    TRANSACTION_SPLIT_DESC,
    TRANSACTION_DISTRIBUTE_DESC,
    TRANSACTION_UNSPLIT_DESC,
    TRANSACTION_UNDISTRIBUTE_DESC,
    TRANSACTION_EXCLUDE,
    TRANSACTION_EXCLUDE_DESC,
    TRANSACTION_INCLUDE,
    TRANSACTION_INCLUDE_DESC,
    TRANSACTION_EXCLUDED_BADGE,
    TRANSACTION_EXCLUDED_SHORT,
    TRANSACTION_AUTO_EXCLUDED_SHORT,
    TRANSACTION_EXCLUDE_SPLIT_WARNING,
    CATEGORY_APPLY_ALL_TITLE,
    CATEGORY_APPLY_ALL,
    CATEGORY_APPLY_JUST_THIS,
    CATEGORY_APPLY_ALL_NOTE,
    CATEGORY_CHOOSE,
    CATEGORY_ALL_CATEGORIES,
    CATEGORY_ALL_SUBCATEGORIES,
    CATEGORY_CHOOSE_SUBCATEGORY,
    CATEGORY_ADD_BUTTON,
    CATEGORY_ADD_SUBCATEGORY,
    CATEGORY_EXPENSE_CATEGORIES,
    CATEGORY_ADD_PRIMARY_DESC,
    CATEGORY_PREVIEW_TITLE,
    CATEGORY_PREVIEW_CONFIRM,
    CATEGORY_PREVIEW_EMPTY,
    CATEGORY_PREVIEW_LOADING,
    CATEGORY_PREVIEW_SELECT_ALL,
    CATEGORY_PREVIEW_DESELECT_ALL,
    // Modify-flow (create/rename/icon) UI — web for now, but shared so mobile can reuse.
    CATEGORY_MODIFY,
    CATEGORY_ADD_PRIMARY,
    CATEGORY_ADD_SUB_TITLE,
    CATEGORY_EDIT_SUB_TITLE,
    CATEGORY_UNDER_PRIMARY,
    CATEGORY_SUB_NAME_PLACEHOLDER,
    CATEGORY_PRIMARY_NAME_LABEL,
    CATEGORY_PRIMARY_NAME_PLACEHOLDER,
    CATEGORY_FIRST_SUB_PLACEHOLDER,
    CATEGORY_COLOUR,
    CATEGORY_CHOOSE_ICON,
    CATEGORY_SUBCATEGORIES_LABEL,
    CATEGORY_FIRST_SUB_ICON_LABEL,
    // Delete-custom-sub dialog
    CATEGORY_DELETE_TITLE,
    CATEGORY_DELETE_USAGE_NONE,
    CATEGORY_DELETE_USAGE_ONE,
    CATEGORY_DELETE_USAGE_MANY,
    CATEGORY_DELETE_MOVE_TO,
    CATEGORY_DELETE_REMOVE_OVERRIDE,
    CATEGORY_DELETE_AFFECTED_HEADER,
    CATEGORY_DELETE_AFFECTED_HEADER_COUNT,
    CATEGORY_DELETE_CASCADE_NOTE,

    // Contextual tour UI (prefix TOUR_ to avoid collision with pre-existing GUIDE_ keys
    // used by the goal-onboarding guide).
    TOUR_OPEN_TOOLTIP,
    TOUR_BACK,
    TOUR_NEXT,
    TOUR_DONE,
    TOUR_CLOSE,
    TOUR_STEP_OF,
    // Transactions-screen tour steps
    TOUR_TXN_NAV_TITLE,
    TOUR_TXN_NAV_BODY,
    TOUR_TXN_SYNC_TITLE,
    TOUR_TXN_SYNC_BODY,
    TOUR_TXN_ACCOUNTS_TITLE,
    TOUR_TXN_ACCOUNTS_BODY,
    TOUR_TXN_ROW_TITLE,
    TOUR_TXN_ROW_BODY,
    TOUR_TXN_MULTISELECT_TITLE,
    TOUR_TXN_MULTISELECT_BODY,
    TOUR_TXN_AI_TITLE,
    TOUR_TXN_AI_BODY,
    TOUR_TXN_MODIFY_TITLE,
    TOUR_TXN_MODIFY_BODY,
    // Dashboard-screen tour steps
    TOUR_DASH_SAVINGS_RATE_TITLE,
    TOUR_DASH_SAVINGS_RATE_BODY,
    TOUR_DASH_MONTHLY_BARS_TITLE,
    TOUR_DASH_MONTHLY_BARS_BODY,
    TOUR_DASH_ACCOUNTS_TITLE,
    TOUR_DASH_ACCOUNTS_BODY,
    TOUR_DASH_CHART_TYPE_TITLE,
    TOUR_DASH_CHART_TYPE_BODY,
    TOUR_DASH_ACTIVE_CHART_TITLE,
    TOUR_DASH_ACTIVE_CHART_BODY,
    TOUR_DASH_GRID_CANVAS_TITLE,
    TOUR_DASH_GRID_CANVAS_BODY,
    TOUR_DASH_RECOMMENDATION_TITLE,
    TOUR_DASH_RECOMMENDATION_BODY,
    TOUR_DASH_GRID_HINT_TITLE,
    TOUR_DASH_GRID_HINT_BODY,
    TOUR_DASH_CUSTOMIZE_TITLE,
    TOUR_DASH_CUSTOMIZE_BODY,
    TOUR_DASH_LAYOUTS_SAVE_TITLE,
    TOUR_DASH_LAYOUTS_SAVE_BODY,
    TOUR_DASH_LAYOUTS_SWITCH_TITLE,
    TOUR_DASH_LAYOUTS_SWITCH_BODY,
    TOUR_DASH_LAYOUT_LOCK_TITLE,
    TOUR_DASH_LAYOUT_LOCK_BODY,
    TOUR_DASH_SAVINGS_TREND_TITLE,
    TOUR_DASH_SAVINGS_TREND_BODY,
    TOUR_DASH_EXPENSE_TIMELINE_TITLE,
    TOUR_DASH_EXPENSE_TIMELINE_BODY,

    // Allocation Studio tour (in-modal 'i' button)
    TOUR_STUDIO_HEADER_TITLE,
    TOUR_STUDIO_HEADER_BODY,
    TOUR_STUDIO_PROGRESS_TITLE,
    TOUR_STUDIO_PROGRESS_BODY,
    TOUR_STUDIO_STRIP_TITLE,
    TOUR_STUDIO_STRIP_BODY,
    TOUR_STUDIO_FORM_TITLE,
    TOUR_STUDIO_FORM_BODY,
    TOUR_STUDIO_CHART_TITLE,
    TOUR_STUDIO_CHART_BODY,
    TOUR_STUDIO_SAVINGS_RATE_TITLE,
    TOUR_STUDIO_SAVINGS_RATE_BODY,
    TOUR_STUDIO_FOOTER_TITLE,
    TOUR_STUDIO_FOOTER_BODY,

    // Debt Paydown Studio tour (its own in-modal 'i' button)
    TOUR_DEBT_STUDIO_HEADER_TITLE,
    TOUR_DEBT_STUDIO_HEADER_BODY,
    TOUR_DEBT_STUDIO_GOAL_TITLE,
    TOUR_DEBT_STUDIO_GOAL_BODY,
    TOUR_DEBT_STUDIO_STRIP_TITLE,
    TOUR_DEBT_STUDIO_STRIP_BODY,
    TOUR_DEBT_STUDIO_LEVERS_TITLE,
    TOUR_DEBT_STUDIO_LEVERS_BODY,
    TOUR_DEBT_STUDIO_CHART_TITLE,
    TOUR_DEBT_STUDIO_CHART_BODY,
    TOUR_DEBT_STUDIO_FOOTER_TITLE,
    TOUR_DEBT_STUDIO_FOOTER_BODY,

    // Goals tour
    TOUR_GOALS_ADD_TITLE,
    TOUR_GOALS_ADD_BODY,
    TOUR_GOALS_CARD_TITLE,
    TOUR_GOALS_CARD_BODY,
    TOUR_GOALS_INSIGHTS_TITLE,
    TOUR_GOALS_INSIGHTS_BODY,
    /** Body shown when the user has Wiz 3R AI cards (narrative populated). */
    TOUR_GOALS_INSIGHTS_BODY_WIZ3R,
    /** Expand-to-sheet affordance on collapsed Wiz 3R cards. */
    TOUR_GOALS_TIPS_SEARCH_TITLE,
    TOUR_GOALS_TIPS_SEARCH_BODY,
    TOUR_GOALS_PIN_TITLE,
    TOUR_GOALS_PIN_BODY,

    // Budget tour
    TOUR_BUDGET_YEAR_TITLE,
    TOUR_BUDGET_YEAR_BODY,
    TOUR_BUDGET_INCOME_TITLE,
    TOUR_BUDGET_INCOME_BODY,
    TOUR_BUDGET_EXPENSE_TITLE,
    TOUR_BUDGET_EXPENSE_BODY,
    TOUR_BUDGET_CELL_TITLE,
    TOUR_BUDGET_CELL_BODY,
    TOUR_BUDGET_COPY_YEAR_TITLE,
    TOUR_BUDGET_COPY_YEAR_BODY,
    TOUR_BUDGET_NET_TITLE,
    TOUR_BUDGET_NET_BODY,

    // AI categorization
    AI_CATEGORIZING,
    AI_PARSE_AND_CATEGORIZE,
    CATEGORY_ASSIGNING,

    // Goals / Recommendations
    GOALS_PERCENT_FUNDED,
    GOALS_REDUCE_BY,
    GOALS_INTEREST_COMPOUND,
    GOALS_INTEREST_SIMPLE,
    GOALS_SPLIT_ACROSS,
    GOALS_DELETE_CONFIRM,

    // Chart insight titles
    CHART_TOTAL_EXPENSES,

    // Settings — linked banks / unlink
    SETTINGS_SAVINGS_TARGET_HELP,
    SETTINGS_CHARTS_REORDER_HELP,
    SETTINGS_LINKED_BANKS,
    SETTINGS_LINKED_BANKS_ERROR,
    SETTINGS_NO_BANKS_LINKED,
    SETTINGS_ACCOUNTS_COUNT,
    SETTINGS_UNLINK,
    SETTINGS_UNLINK_CONFIRM_TITLE,
    SETTINGS_UNLINK_WARNING,
    SETTINGS_BIOMETRIC_UNLOCK,

    SETTINGS_RATE_APP,
    SETTINGS_RATE_APP_DESC,
    SETTINGS_BIOMETRIC_TITLE,
    SETTINGS_PRIVACY_DESC,
    SETTINGS_SECURITY_DESC,
    SETTINGS_GUIDE_DESC,
    SETTINGS_UNLINK_ACCOUNTS_REMOVED,
    SETTINGS_ABOUT_APP,

    // About page
    ABOUT_TITLE,
    ABOUT_MISSION_TITLE,
    ABOUT_MISSION,
    ABOUT_PHILOSOPHY_TITLE,
    ABOUT_PHILOSOPHY_INTRO,
    ABOUT_SAVINGS_RATE_FORMULA,
    ABOUT_SAVINGS_RATE_EXPLANATION,
    ABOUT_QUOTE,
    ABOUT_HOW_HELPS_TITLE,
    ABOUT_HOW_HELPS_INTRO,
    ABOUT_HELP_VISIBILITY,
    ABOUT_HELP_INSIGHTS,
    ABOUT_HELP_HABITS,
    ABOUT_HELP_GOALS,
    ABOUT_STORY_TITLE,
    ABOUT_STORY_P1,
    ABOUT_STORY_P2,
    ABOUT_STORY_P3,
    ABOUT_VALUES_TITLE,
    ABOUT_VALUE_SIMPLICITY,
    ABOUT_VALUE_PRIVACY,
    ABOUT_VALUE_PRICING,
    ABOUT_VALUE_RESULTS,
    ABOUT_CONTACT_TITLE,
    ABOUT_CONTACT_INTRO,

    // Statement Accounts (imported from bank statements)
    SETTINGS_STATEMENT_ACCOUNTS,
    SETTINGS_NO_STATEMENT_ACCOUNTS,
    SETTINGS_STATEMENT_ACCOUNTS_ERROR,
    SETTINGS_DELETE_STATEMENT_ACCOUNT_TITLE,
    SETTINGS_DELETE_STATEMENT_ACCOUNT_WARNING,
    SETTINGS_DELETE_STATEMENT_ACCOUNT_CONFIRM,
    SETTINGS_STATEMENT_TRANSACTIONS_COUNT,

    // Chart names (for settings visibility picker)
    CHART_NAME_PREVIOUS_YEAR,
    CHART_NAME_SAVINGS_TREND,
    CHART_NAME_EXPENSE_TIMELINE,
    CHART_NAME_EXPENSE_DISTRIBUTION,
    CHART_NAME_CATEGORY_SPENDING,

    // Subscription detection
    SUBSCRIPTION_CHECK_TITLE,
    SUBSCRIPTION_CHECK_SUBTITLE,
    SUBSCRIPTION_NONE_DETECTED,
    SUBSCRIPTION_MONTHLY_TOTAL,
    SUBSCRIPTION_WEEKLY,
    SUBSCRIPTION_BIWEEKLY,
    SUBSCRIPTION_MONTHLY,
    SUBSCRIPTION_CONFIRM_SELECTION,

    // Multi-select
    MULTISELECT_COUNT,
    MULTISELECT_CHANGE_CATEGORY,
    MULTISELECT_ASSIGN_TO_GOAL,
    MULTISELECT_EXCLUDE,
    MULTISELECT_CLEAR_PROMPT,
    MULTISELECT_CLEAR,
    MULTISELECT_KEEP,
    MULTISELECT_RECATEGORIZED,
    MULTISELECT_ASSIGNED_TO_GOAL,
    MULTISELECT_EXCLUDED,
    MULTISELECT_INCLUDE,
    MULTISELECT_INCLUDED,
    MULTISELECT_EXCLUDE_CONFIRM,
    MULTISELECT_PARTIAL_FAILURE,

    // Multi-select batch actions
    BATCH_EXCLUDE_COUNT,
    BATCH_INCLUDE_COUNT,

    // Contexts (Pro feature)
    CONTEXT_TITLE,
    CONTEXT_ALL,
    CONTEXT_ACTION_TITLE,
    CONTEXT_ACTION_SUBTITLE,
    CONTEXT_ASSIGN_TITLE,
    CONTEXT_ASSIGN_SUBTITLE,
    CONTEXT_NAME_PLACEHOLDER,
    CONTEXT_EDIT_TITLE,
    CONTEXT_DELETE_CONFIRM,
    CONTEXT_DELETE_CONFIRM_MESSAGE,
    /** Naming and deleting a saved dashboard layout. */
    DASHBOARD_LAYOUT_NAME,
    DASHBOARD_LAYOUT_DELETE_CONFIRM,
    CONTEXT_SHOWING_BOTH,
    CONTEXT_SHOWING_ALL_N,
    CONTEXT_EXPENSES_ONLY,
    CONTEXT_BATCH_ASSIGN,

    // Manual Account / Statement Import
    IMPORT_FROM_STATEMENTS,
    IMPORT_RECOMMEND_DIRECT,
    IMPORT_CSV_OR_PDF,
    IMPORT_ANALYZING,
    IMPORT_DETECTED,
    IMPORT_IS_CORRECT,
    IMPORT_LOOKS_GOOD,
    IMPORT_DOESNT_LOOK_RIGHT,
    IMPORT_STATEMENT,
    IMPORT_MISMATCH_WARNING,
    IMPORT_ANYWAY,
    IMPORT_SUCCESS,
    IMPORT_DUPLICATES_SKIPPED,
    IMPORT_INSTITUTION_NAME,
    IMPORT_ACCOUNT_NAME,
    IMPORT_ACCOUNT_TYPE,
    IMPORT_TRANSACTIONS_PREVIEW,
    IMPORT_TOTAL_COUNT,
    IMPORT_DROP_FILE,
    IMPORT_SELECT_FILE,
    IMPORT_SUPPORTED_FORMATS,
    IMPORT_PRIVACY_NOTE,
    IMPORT_RETRY,
    IMPORT_CANCELLED_MESSAGE,
    IMPORT_FAILED_GENERIC,
    IMPORT_MISMATCH_UNKNOWN,
    IMPORT_PASTE_MANUALLY,
    IMPORT_COLUMN_DATE,
    IMPORT_COLUMN_DESCRIPTION,
    IMPORT_COLUMN_AMOUNT,
    IMPORT_ACCOUNT_TYPE_CHECKING,
    IMPORT_ACCOUNT_TYPE_SAVINGS,
    IMPORT_ACCOUNT_TYPE_CREDIT,

    // Tooltips
    TOOLTIP_GO_HOME,
    TOOLTIP_CHANGE_LANGUAGE,
    TOOLTIP_HOW_IT_WORKS,

    // Time periods (display labels)
    TIME_WEEKLY,
    TIME_MONTHLY,
    TIME_QUARTERLY,

    // Transaction Notifications
    NOTIF_SETTINGS_TITLE,
    NOTIF_SETTINGS_ENABLE,
    NOTIF_SETTINGS_CHANNEL,
    NOTIF_SETTINGS_EMAIL,
    NOTIF_SETTINGS_SMS,
    NOTIF_SETTINGS_BOTH,
    NOTIF_SETTINGS_PHONE,
    NOTIF_SETTINGS_VERIFY,
    NOTIF_SETTINGS_PHONE_VERIFIED,
    NOTIF_SETTINGS_SMS_CONSENT,
    NOTIF_SETTINGS_SMS_AGREE,
    NOTIF_SETTINGS_UPGRADE_HINT,
    NOTIF_SETTINGS_ENTER_CODE,
    NOTIF_SETTINGS_CODE_SENT,
    /** The "send me one now" button in Settings, and what it reports back. */
    NOTIF_TEST_SEND,
    NOTIF_TEST_SENDING,
    NOTIF_TEST_SENT,
    NOTIF_TEST_NOTHING_FIRED,
    NOTIF_TEST_FAILED,
    NOTIF_EMAIL_SUBJECT,
    NOTIF_EMAIL_GREETING,
    NOTIF_EMAIL_SUMMARY,
    NOTIF_EMAIL_SUMMARY_SINGLE_ACCOUNT,
    NOTIF_EMAIL_CONFIRM_ALL,
    NOTIF_EMAIL_REVIEW,
    NOTIF_EMAIL_DISMISS,
    NOTIF_EMAIL_FOOTER,
    NOTIF_SMS_SUMMARY,
    NOTIF_SMS_CONFIRM_DONE,
    NOTIF_SMS_REVIEW_LINK,
    NOTIF_SMS_DISMISS_ACK,
    NOTIF_SMS_HELP,
    NOTIF_SMS_NO_PENDING,
    NOTIF_SMS_EXPIRED,
    NOTIF_SMS_ALREADY_ACTED,
    NOTIF_SMS_DETAILS_FOOTER,
    NOTIF_ACTION_CONFIRMED_TITLE,
    NOTIF_ACTION_CONFIRMED_MSG,
    NOTIF_ACTION_DISMISSED_TITLE,
    NOTIF_ACTION_EXPIRED_TITLE,
    NOTIF_ACTION_EXPIRED_MSG,
    NOTIF_ACTION_ALREADY_ACTED_TITLE,

    // Subscription status card (Settings)
    SETTINGS_PLAN_PRO,
    SETTINGS_PLAN_STANDARD,
    SETTINGS_PLAN_TRIAL,
    SETTINGS_PLAN_ACTIVE,
    SETTINGS_PLAN_CANCELED,
    SETTINGS_PLAN_EXPIRED,
    SETTINGS_PLAN_PAST_DUE,
    SETTINGS_MANAGE_SUBSCRIPTION,
    SETTINGS_PLAN_ALL_FEATURES,
    SETTINGS_PLAN_TRIAL_ENJOY,
    SETTINGS_PLAN_CHOOSE,
    SETTINGS_PLAN_UPGRADE_DESC,
    SETTINGS_PLAN_UPGRADE_CTA,
    SETTINGS_PLAN_THANK_YOU,
    SETTINGS_DELETE_SOFT_CONFIRM,
    SETTINGS_DELETE_YES_DELETE,

    // Phase B merge — restoring history from previously-unlinked accounts
    MERGE_RESTORED_TOAST_ONE,
    MERGE_RESTORED_TOAST_MANY,
    MERGE_PENDING_CONFIRM_PROMPT,

    // Institution health (upstream Plaid/bank outage detection)
    INSTITUTION_DOWN_TITLE,
    INSTITUTION_DOWN_BODY,
    INSTITUTION_DEGRADED_TITLE,
    INSTITUTION_DEGRADED_BODY,
    INSTITUTION_TRY_ANYWAY,
    INSTITUTION_WAIT,

    // Wiz 3R AI cards — section labels and detail page UI strings
    WIZ3R_SECTION_REVEAL,
    WIZ3R_SECTION_REASON,
    WIZ3R_SECTION_REALIZE,
    WIZ3R_DETAIL_BACK,
    WIZ3R_ASK,
    // The advice card's doorway into the detail view. Distinct from WIZ3R_ASK,
    // which opens the chat from *inside* the detail — there the reasoning is
    // already on screen, so "see why" would be wrong.
    WIZ3R_CARD_SEE_WHY,
    /** The same doorway on a platform with no chat to offer. Web has
     *  WebWiz3RChatPanel; Android and iOS have nothing to open, so the CTA
     *  there must not promise questions it cannot accept. Fold this back into
     *  WIZ3R_CARD_SEE_WHY once mobile has a chat. */
    WIZ3R_CARD_SEE_WHY_NO_ASK,
    WIZ3R_ASK_COMING_SOON,
    WIZ3R_LOADING_SOURCE_DATA,
    WIZ3R_KEY_INPUTS,
    // Card-specific labels for the KEY INPUTS rows. CardMetrics.amount and
    // .previousSavingsRate hold a different quantity on every card type, so a
    // single generic label states the wrong thing — see buildReason.
    WIZ3R_REASON_YEARLY_PROJECTION,
    WIZ3R_REASON_PREVIOUS_BEST,
    WIZ3R_REASON_EXTRA_SAVED,
    WIZ3R_REASON_SAVED_SO_FAR,
    WIZ3R_REASON_REMAINING_TO_GOAL,
    WIZ3R_CARD_NOT_LOADED,
    WIZ3R_CARD_NOT_LOADED_BODY,
    WIZ3R_BACK_TO_DASHBOARD,
    // REVEAL builder strings
    WIZ3R_REVEAL_GOAL_TXNS_HEADING,
    WIZ3R_REVEAL_GOAL_TXNS_SUBHEADING,
    WIZ3R_REVEAL_GOAL_TXNS_EMPTY,
    WIZ3R_REVEAL_THIS_MONTH,
    WIZ3R_REVEAL_LAST_MONTH,
    WIZ3R_REVEAL_VS_LAST_MONTH,
    WIZ3R_REVEAL_CATEGORY_HEADING,
    WIZ3R_REVEAL_CATEGORY_HEADING_GENERIC,
    WIZ3R_REVEAL_CATEGORY_SUBHEADING,
    WIZ3R_REVEAL_CATEGORY_TOTAL_THIS_MONTH,
    WIZ3R_REVEAL_CATEGORY_SHOWN,
    WIZ3R_REVEAL_CATEGORY_EMPTY,
    WIZ3R_REVEAL_GOAL_PROGRESS_HEADING,
    WIZ3R_REVEAL_GOAL_PROGRESS_HEADING_GENERIC,
    WIZ3R_REVEAL_GOAL_PROGRESS_SUBHEADING,
    WIZ3R_REVEAL_GOAL_PROGRESS_EMPTY,
    WIZ3R_REVEAL_CURRENT_BALANCE,
    WIZ3R_REVEAL_TARGET,
    WIZ3R_REVEAL_FUNDED,
    WIZ3R_REVEAL_MERCHANT_HEADING,
    WIZ3R_REVEAL_RECURRING_HEADING,
    WIZ3R_REVEAL_RECURRING_SUBHEADING,
    WIZ3R_REVEAL_NO_TRANSACTIONS,
    WIZ3R_REVEAL_GENERIC_HEADING,
    WIZ3R_REVEAL_GENERIC_EMPTY,
    // REASON builder strings
    WIZ3R_REASON_SAVINGS_RATE_THIS,
    WIZ3R_REASON_SAVINGS_RATE_PREV,
    WIZ3R_REASON_PERCENT_OF_INCOME,
    WIZ3R_REASON_PERCENT_OF_BUDGET,
    WIZ3R_REASON_PLANNED_AMOUNT,
    WIZ3R_REASON_CHANGE_VS_PRIOR,
    WIZ3R_REASON_AMOUNT,
    WIZ3R_REASON_GOAL_FUNDED,
    WIZ3R_REASON_EXPLAINER_SAVINGS,
    WIZ3R_REASON_EXPLAINER_CATEGORY,
    WIZ3R_REASON_EXPLAINER_PROJECTION,
    // REALIZE builder strings
    WIZ3R_REALIZE_REVIEW_TXNS,
    WIZ3R_REALIZE_SEE_CATEGORY,
    WIZ3R_REALIZE_OPEN_GOAL,
    WIZ3R_REALIZE_CREATE_GOAL,
    WIZ3R_REALIZE_REVIEW_SUBSCRIPTIONS,
    WIZ3R_REALIZE_READ_GUIDE,
    WIZ3R_REALIZE_OPEN_GOALS,
    WIZ3R_REALIZE_SET_BUDGET,
    WIZ3R_REALIZE_TAG_MORE_TRANSFERS,
    WIZ3R_REALIZE_INTRO_PERSONAL_BEST,
    WIZ3R_REALIZE_INTRO_SAVINGS_DROP,
    WIZ3R_REALIZE_INTRO_CATEGORY,
    WIZ3R_REALIZE_INTRO_ALMOST_COMPLETE,
    WIZ3R_REALIZE_INTRO_BEHIND,

    // Wiz 3R chat panel
    WIZ3R_CHAT_TITLE,
    WIZ3R_CHAT_INPUT_PLACEHOLDER,
    WIZ3R_CHAT_SEND,
    WIZ3R_CHAT_CLEAR,
    WIZ3R_CHAT_CLEAR_CONFIRM,
    WIZ3R_CHAT_EMPTY_PROMPT_TITLE,
    WIZ3R_CHAT_EMPTY_PROMPT_BODY,
    WIZ3R_CHAT_EMPTY_SUGGESTION_1,
    WIZ3R_CHAT_EMPTY_SUGGESTION_2,
    WIZ3R_CHAT_EMPTY_SUGGESTION_3,
    WIZ3R_CHAT_THINKING,
    WIZ3R_CHAT_RATE_LIMITED,
    WIZ3R_CHAT_PRO_REQUIRED,
    WIZ3R_CHAT_NETWORK_ERROR,
    WIZ3R_CHAT_DEMO_SIGNUP,
    WIZ3R_CHAT_CLOSE,

    // 50/30/20 budget buckets (Phase 3.1)
    BUDGET_BUCKET_NEEDS,
    BUDGET_BUCKET_WANTS,
    BUDGET_BUCKET_SAVINGS,
    BUDGET_BUCKET_IGNORE,
    BUDGET_BUCKET_UNCLASSIFIED,
    BUDGET_BUCKET_CHIP_TITLE,
    BUDGET_BUCKET_DEFAULT_HINT,
    BUDGET_BUCKET_OVERRIDE_HINT,
    BUDGET_BUCKET_MIXED,
    BUDGET_BUCKET_MIXED_HINT,
    BUDGET_BUCKET_RESET,
    BUDGET_BUCKET_FOLLOWS_PRIMARY,
    // Needs & Wants Flow widget — edit-buckets modal (Task 7)
    BUDGET_EDIT_BUCKETS_CTA,
    BUDGET_EDIT_BUCKETS_TITLE,
    BUDGET_EDIT_BUCKETS_SHOW_ALL,
    // Budget primary↔sub source-of-truth transition modal
    BUDGET_SWITCH_TO_SUB_TITLE,
    BUDGET_SWITCH_TO_SUB_BODY,
    BUDGET_SWITCH_TO_SUB_CONFIRM,
    BUDGET_SWITCH_TO_PRIMARY_TITLE,
    BUDGET_SWITCH_TO_PRIMARY_BODY,
    BUDGET_SWITCH_TO_PRIMARY_CONFIRM,
    BUDGET_TRANSITION_CANCEL,
    BUDGET_PRIMARY_SUM_LABEL,
    BUDGET_PRIMARY_SUM_HINT,

    // Goal Allocation Studio — onboarding stepper (§14)
    STUDIO_PREVIEW_BANNER_TITLE,
    STUDIO_PREVIEW_BANNER_BODY,
    STUDIO_PREVIEW_BANNER_CTA,
    STUDIO_PREVIEW_BANNER_EDIT_CTA,
    STUDIO_STEPPER_PROGRESS,
    STUDIO_STEPPER_SKIP,
    STUDIO_STEPPER_BACK,
    STUDIO_STEPPER_DONE_TITLE,
    STUDIO_STEPPER_DONE_BODY,
    STUDIO_STEPPER_DONE_DISMISS,
    STUDIO_WHY_LABEL,
    STUDIO_WHY_INCOME,
    STUDIO_WHY_AGE,
    STUDIO_WHY_HOME,
    STUDIO_WHY_RRSP_ROOM,
    STUDIO_WHY_TFSA_ROOM,
    STUDIO_WHY_TENURE,
    STUDIO_WHY_PRIORITIES,
    STUDIO_HOW_LABEL,
    STUDIO_HOW_FIND_ROOM,
    STUDIO_Q_INCOME_TITLE,
    STUDIO_Q_INCOME_PROVINCE_LABEL,
    STUDIO_Q_INCOME_MODE_GROSS,
    STUDIO_Q_INCOME_MODE_PAYCHEQUE,
    STUDIO_Q_INCOME_MODE_BANK,
    STUDIO_Q_INCOME_MODE_BANK_COMING_SOON,
    STUDIO_Q_INCOME_GROSS_LABEL,
    STUDIO_Q_INCOME_PAYCHEQUE_LABEL,
    STUDIO_Q_INCOME_FREQUENCY_LABEL,
    STUDIO_Q_INCOME_FREQUENCY_MONTHLY,
    STUDIO_Q_INCOME_FREQUENCY_BI_WEEKLY,
    STUDIO_Q_INCOME_ESTIMATED_GROSS,
    STUDIO_Q_AGE_TITLE,
    STUDIO_Q_AGE_18_29,
    STUDIO_Q_AGE_30_39,
    STUDIO_Q_AGE_40_49,
    STUDIO_Q_AGE_50_59,
    STUDIO_Q_AGE_60_69,
    STUDIO_Q_AGE_70_PLUS,
    STUDIO_Q_HOME_TITLE,
    STUDIO_Q_HOME_NEVER,
    STUDIO_Q_HOME_BEFORE,
    STUDIO_Q_HOME_DECLINED,
    STUDIO_Q_RRSP_ROOM_TITLE,
    STUDIO_Q_TFSA_ROOM_TITLE,
    STUDIO_Q_ROOM_KNOWN,
    STUDIO_Q_ROOM_ROUGH,
    STUDIO_Q_ROOM_UNKNOWN,
    STUDIO_Q_ROOM_AMOUNT_LABEL,
    STUDIO_Q_TENURE_TITLE,
    STUDIO_Q_TENURE_NEVER,
    STUDIO_Q_TENURE_FEW_MONTHS,
    STUDIO_Q_TENURE_ONE_TO_THREE,
    STUDIO_Q_TENURE_FIVE_PLUS,
    STUDIO_Q_PRIORITIES_TITLE,
    STUDIO_Q_PRIORITIES_HINT,
    STUDIO_Q_PRIORITY_EMERGENCY,
    STUDIO_Q_PRIORITY_HOME,
    STUDIO_Q_PRIORITY_RETIREMENT,
    STUDIO_Q_PRIORITY_EDUCATION,
    STUDIO_Q_PRIORITY_NONE,
    STUDIO_Q_PRIORITIES_CONTINUE,
    STUDIO_ROOM_CRA_LINK,
    STUDIO_ROOM_LEAVE_BLANK_NOTE,
    STUDIO_Q_INCOME_PROVINCE_PLACEHOLDER,
    // Studio screen (Vehicle Comparison Strip + Lever Panel)
    STUDIO_CUSTOMIZE_ALLOCATION,
    STUDIO_HEADER_TITLE,
    STUDIO_HEADER_EDIT_PROFILE,
    STUDIO_VEHICLE_TFSA,
    STUDIO_VEHICLE_RRSP,
    STUDIO_VEHICLE_FHSA,
    STUDIO_VEHICLE_RESP,
    STUDIO_VEHICLE_RDSP,
    STUDIO_VEHICLE_NON_REGISTERED,
    STUDIO_VEHICLE_HISA,
    STUDIO_VEHICLE_HISA_INFLATION_NOTE,
    STUDIO_ACCOUNT_TYPE_SECTION_TITLE,
    // Goal edit/create — dropdown labels (account types + category helper text + "not set" placeholder)
    GOAL_CATEGORY_LABEL,
    GOAL_CATEGORY_HELPER,
    GOAL_ACCOUNT_NOT_SET,
    GOAL_ACCOUNT_LABEL_HISA,
    GOAL_ACCOUNT_LABEL_TFSA,
    GOAL_ACCOUNT_LABEL_RRSP,
    GOAL_ACCOUNT_LABEL_FHSA,
    GOAL_ACCOUNT_LABEL_RESP,
    GOAL_ACCOUNT_LABEL_RDSP,
    GOAL_ACCOUNT_LABEL_NON_REGISTERED,
    GOAL_ACCOUNT_LABEL_EMPLOYER_PENSION,
    GOAL_ACCOUNT_LABEL_CHEQUING,
    GOAL_ACCOUNT_LABEL_OTHER,
    GOAL_DEBT_DETAILS_TITLE,
    GOAL_DEBT_DETAILS_RATE,
    GOAL_DEBT_DETAILS_BALANCE,
    GOAL_DEBT_DETAILS_MONTHS,
    STUDIO_BADGE_BEST_FIT,
    STUDIO_BADGE_BACKUP,
    STUDIO_BADGE_NOT_IDEAL,
    STUDIO_BADGE_INELIGIBLE,
    STUDIO_NET_AT_GOAL,
    STUDIO_ROOM_REMAINING,
    STUDIO_LEVER_CONTRIBUTION,
    STUDIO_LEVER_RETURN,
    STUDIO_LEVER_HORIZON,
    STUDIO_LEVER_HORIZON_SET,
    STUDIO_LEVER_FUTURE_RATE,
    STUDIO_LEVER_RESET,
    STUDIO_PIN_VEHICLE,
    STUDIO_PINNED_VEHICLE,
    STUDIO_YOUR_PICK,
    STUDIO_NO_PROFILE_TITLE,
    STUDIO_NO_PROFILE_BODY,
    STUDIO_NO_VEHICLES_EMPTY_STATE,
    STUDIO_CHART_TITLE,
    STUDIO_CHART_LEGEND_NET_HINT,
    STUDIO_CHART_INCL_REFUNDS,
    STUDIO_CHART_NET,
    STUDIO_CHART_DASHED_NOTE,
    STUDIO_OTHER_GOALS_NOTE,
    STUDIO_ROOM_CHART_TITLE,
    STUDIO_ROOM_CHART_REMAINING,
    DEBT_STUDIO_TITLE,
    DEBT_STUDIO_CUSTOMIZE_CTA,
    DEBT_STUDIO_SCENARIO_CURRENT,
    DEBT_STUDIO_SCENARIO_ADJUSTED,
    DEBT_STUDIO_LEVER_PAYMENT,
    DEBT_STUDIO_LEVER_RATE,
    DEBT_STUDIO_LEVER_LUMPSUM,
    DEBT_STUDIO_STAT_PAYOFF,
    DEBT_STUDIO_STAT_INTEREST,
    DEBT_STUDIO_STAT_TOTAL,
    DEBT_STUDIO_CHART_TITLE,
    DEBT_STUDIO_CHART_HINT,
    DEBT_STUDIO_NEGATIVE_AMORT_WARNING,
    DEBT_STUDIO_PHASE2_NOTE,
    // Twin Plan A/B redesign (2026-06-15)
    DEBT_STUDIO_PLAN_A,
    DEBT_STUDIO_PLAN_B,
    DEBT_STUDIO_FREQ_MONTHLY,
    DEBT_STUDIO_FREQ_SEMI_MONTHLY,
    DEBT_STUDIO_FREQ_ACCEL_BIWEEKLY,
    DEBT_STUDIO_LEVER_PAYMENT_MONTHLY,
    DEBT_STUDIO_LEVER_PAYMENT_SEMI_MONTHLY,
    DEBT_STUDIO_LEVER_PAYMENT_ACCEL_BIWEEKLY,
    DEBT_STUDIO_PREPAY_NOTE,
    DEBT_STUDIO_DELTA_SAVES,
    DEBT_STUDIO_DELTA_FINISHES_SOONER,
    DEBT_STUDIO_APPLY_TO_GOAL,
    DEBT_STUDIO_APPLIED_CHIP,
    DEBT_STUDIO_APPLIED_TOAST,
    DEBT_STUDIO_UNDO,
    STUDIO_SAVINGS_RATE_TITLE,
    STUDIO_SAVINGS_RATE_OUT_OF_POCKET,
    STUDIO_SAVINGS_RATE_TAX_SHIFTED,
    STUDIO_SAVINGS_RATE_EXPLAINER,
    STUDIO_DISCLAIMER,
    // Goal Allocation Studio — merged goal-edit form additions.
    STUDIO_UNSAVED_CHIP,
    STUDIO_FORM_GOAL_NAME_LABEL,
    STUDIO_FORM_CATEGORY_LABEL,
    STUDIO_FORM_TARGET_LABEL,
    STUDIO_FORM_DEADLINE_LABEL,
    STUDIO_FORM_TARGET_DATE_INLINE,
    STUDIO_PROGRESS_TITLE,
    STUDIO_SAVE_ACTION,
    STUDIO_CANCEL_ACTION,
    STUDIO_CANCEL_TOOLTIP,
    STUDIO_DELETE_GOAL,
    // Goal type picker (replaces the old big "Add new goal" form).
    GOAL_TYPE_PICKER_TITLE,
    GOAL_TYPE_PICKER_SAVINGS,
    GOAL_TYPE_PICKER_DEBT,
    // Debt Studio inline goal-data row.
    DEBT_STUDIO_GOAL_NAME_LABEL,
    DEBT_STUDIO_GOAL_BALANCE_LABEL,
    DEBT_STUDIO_GOAL_RATE_LABEL,
    DEBT_STUDIO_GOAL_TERM_LABEL,
    // Hover tooltip on the plan-selection checkbox — explains that ticking
    // the checkbox applies that plan as the user's active strategy.
    DEBT_STUDIO_PLAN_TOOLTIP,

    // Goal Lab — the standalone full-screen scenario/timeline explorer (the
    // studio, decoupled from goal creation). Reached from a button on the Goals
    // page and a CTA in the simple create/edit menus.
    LAB_TITLE,
    LAB_NAV_BUTTON,
    GOAL_FORM_LAB_CTA_LABEL,
    GOAL_FORM_LAB_CTA_BODY,
    // "Apply to goal" export flow — the Lab pushes new values back to a goal
    // via an explicit confirm that lists exactly which fields change.
    LAB_APPLY_TO_GOAL,
    LAB_APPLY_TITLE,
    LAB_APPLY_CONFIRM,
    LAB_APPLY_NO_CHANGES,
    LAB_SAVE_AS_GOAL,
    LAB_CLOSE,
    LAB_FIELD_TARGET,
    LAB_FIELD_DEADLINE,
    LAB_FIELD_MONTHLY,
    LAB_FIELD_ACCOUNT,

    // Dashboard insight pool (recommendations/DashboardInsights.kt) — the
    // secondary lines the Recommendation widget shows when it is tall enough.
    // The PRIMARY line still uses the DASHBOARD_* keys above.
    INSIGHT_BUDGET_OVERAGE_TITLE,
    INSIGHT_BUDGET_OVERAGE_DETAIL,
    INSIGHT_MOVER_UP_TITLE,
    INSIGHT_MOVER_DOWN_TITLE,
    INSIGHT_MOVER_DETAIL,
    INSIGHT_GOAL_BEHIND_TITLE,
    INSIGHT_GOAL_BEHIND_DETAIL,
    INSIGHT_YOY_UP_TITLE,
    INSIGHT_YOY_DOWN_TITLE,
    INSIGHT_YOY_DETAIL,
    INSIGHT_LARGEST_CATEGORY_TITLE,
    INSIGHT_LARGEST_CATEGORY_DETAIL,
    INSIGHT_STREAK_TITLE,
    INSIGHT_STREAK_DETAIL,
    // Recommendation-widget chrome for the expanded tiers.
    INSIGHT_ALSO_WORTH_NOTING,
    INSIGHT_AVAILABLE_TO_SAVE_LABEL,
    INSIGHT_OVER_BUDGET_LABEL,
    // Guidance under the over-budget hero. Deliberately states NO amount — the
    // hero above it already does, and repeating it makes the card restate itself.
    INSIGHT_OVER_BUDGET_GUIDANCE,
}
